import { useState, useEffect } from 'react';
import { useCart } from '@/hooks/useCart';

interface HeroProps {
  onCartOpen: () => void;
  onTierChange?: (tier: string) => void;
}

export default function Hero({ onCartOpen, onTierChange }: HeroProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { totalItems } = useCart();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: '全部商品', href: '#products', category: 'all', tier: null },
    { label: '豬肉', href: '#products', category: 'pork', tier: null },
    { label: '牛肉', href: '#products', category: 'beef', tier: null },
    { label: '雞肉', href: '#products', category: 'chicken', tier: null },
    { label: '羊肉', href: '#products', category: 'lamb', tier: null },
    { label: '單品', href: '#products', category: null, tier: 'single' },
    { label: '套餐', href: '#products', category: null, tier: 'set' },
    { label: '高端', href: '#products', category: null, tier: 'premium' },
    { label: '關於我們', href: '#about', category: null, tier: null },
  ];

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    link: { href: string; category: string | null; tier: string | null }
  ) => {
    e.preventDefault();
    if (link.tier !== null) {
      onTierChange?.(link.tier);
    } else if (link.category !== null) {
      window.location.hash = `products?category=${link.category}`;
    } else {
      window.location.hash = link.href.replace('#', '');
    }
    const targetId = link.href.replace('#', '');
    const el = document.getElementById(targetId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="w-full px-6 lg:px-16 py-4 flex items-center justify-between">
          <a href="#" className="cursor-pointer">
            <h1
              className={`text-2xl font-black tracking-tight ${
                scrolled ? 'text-[#1a1a1a]' : 'text-white'
              }`}
            >
              鮮肉本舖
            </h1>
            <p className={`text-xs tracking-widest ${scrolled ? 'text-[#8B4513]' : 'text-white/70'}`}>
              FRESH MEAT MARKET
            </p>
          </a>

          <div className="hidden md:flex items-center space-x-6">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => handleNavClick(e, link)}
                className={`text-sm font-medium hover:opacity-70 transition-opacity whitespace-nowrap cursor-pointer ${
                  link.tier === 'premium'
                    ? scrolled
                      ? 'text-amber-600 font-bold'
                      : 'text-[#D4A96A] font-bold'
                    : scrolled
                    ? 'text-[#1a1a1a]'
                    : 'text-white'
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={onCartOpen}
              className={`relative flex items-center space-x-2 px-4 py-2 rounded-full font-medium text-sm transition-all cursor-pointer whitespace-nowrap ${
                scrolled
                  ? 'bg-[#8B4513] text-white hover:bg-[#6B3410]'
                  : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
              }`}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-shopping-cart-2-line text-base"></i>
              </span>
              <span>購物車</span>
              {totalItems > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </button>
            <button
              className={`md:hidden w-9 h-9 flex items-center justify-center cursor-pointer ${
                scrolled ? 'text-[#1a1a1a]' : 'text-white'
              }`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => {
                  handleNavClick(e, link);
                  setMobileMenuOpen(false);
                }}
                className={`block py-3 text-sm font-medium border-b border-gray-50 cursor-pointer ${
                  link.tier === 'premium' ? 'text-amber-600 font-bold' : 'text-[#1a1a1a]'
                }`}
              >
                {link.label}
              </a>
            ))}
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <div className="relative min-h-screen overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/d5f1cebd17c39e75a611e2256ac8720f.jpg"
            alt="Premium Fresh Meat"
            className="w-full h-full object-cover object-center md:object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-black/20"></div>
        </div>

        <div className="relative z-10 min-h-screen flex items-center">
          <div className="w-full px-6 lg:px-16 pt-24">
            <div className="max-w-2xl">
              <p className="text-white/70 text-xs uppercase tracking-[0.4em] mb-5 font-medium">
                嚴選優質肉品 · 直送到府
              </p>
              <h2 className="text-white font-black leading-[0.9] tracking-tight mb-6">
                <span className="block text-6xl lg:text-8xl">頂級</span>
                <span className="block text-6xl lg:text-8xl text-[#D4A96A]">鮮肉</span>
                <span className="block text-4xl lg:text-5xl font-light mt-2">嚴選直送</span>
              </h2>
              <p className="text-white/70 text-base leading-relaxed mb-10 max-w-md">
                精選台灣在地及進口優質肉品，每日新鮮到貨，嚴格把關品質，讓您在家也能享受餐廳級的美味。
              </p>

              {/* Price Tier Quick Links */}
              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  { label: '單品 NT$300起', tier: 'single', icon: 'ri-leaf-line' },
                  { label: '套餐 NT$1,000起', tier: 'set', icon: 'ri-gift-line' },
                  { label: '高端 NT$5,000起', tier: 'premium', icon: 'ri-vip-crown-line' },
                ].map((item) => (
                  <button
                    key={item.tier}
                    onClick={() => {
                      onTierChange?.(item.tier);
                      const el = document.getElementById('products');
                      if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer whitespace-nowrap border ${
                      item.tier === 'premium'
                        ? 'bg-[#D4A96A]/20 border-[#D4A96A]/50 text-[#D4A96A] hover:bg-[#D4A96A]/30'
                        : 'bg-white/10 border-white/30 text-white hover:bg-white/20'
                    }`}
                  >
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${item.icon} text-sm`}></i>
                    </span>
                    {item.label}
                  </button>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="#products"
                  className="inline-flex items-center justify-center bg-[#8B4513] text-white px-8 py-4 rounded-full font-bold text-sm uppercase tracking-wide hover:bg-[#6B3410] transition-all cursor-pointer whitespace-nowrap"
                >
                  立即選購
                  <span className="ml-3 w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-arrow-right-line text-base"></i>
                  </span>
                </a>
                <a
                  href="#about"
                  className="inline-flex items-center justify-center border border-white/50 text-white px-8 py-4 rounded-full font-medium text-sm hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
                >
                  了解更多
                </a>
              </div>

              {/* Stats */}
              <div className="mt-16 flex items-center space-x-10">
                <div>
                  <p className="text-white text-3xl font-black">10+</p>
                  <p className="text-white/60 text-xs mt-1">年品質保證</p>
                </div>
                <div className="w-px h-10 bg-white/20"></div>
                <div>
                  <p className="text-white text-3xl font-black">50+</p>
                  <p className="text-white/60 text-xs mt-1">精選品項</p>
                </div>
                <div className="w-px h-10 bg-white/20"></div>
                <div>
                  <p className="text-white text-3xl font-black">5000+</p>
                  <p className="text-white/60 text-xs mt-1">滿意顧客</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center animate-bounce">
          <i className="ri-arrow-down-line text-white/50 text-2xl"></i>
        </div>
      </div>
    </>
  );
}
